import axios from 'axios';
import {
  isBackendUnreachable,
  shouldClearSessionOnError,
} from '@/lib/auth/classifySessionError';
import {
  notifyBackendAvailable,
  notifyBackendUnavailable,
} from '@/lib/auth/backendAvailability';
import { clearProfilePicture } from '@/lib/profilePictureCache';

// Base axios instance for non-authenticated requests (like login, register)
export const publicAxios = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Authenticated axios instance for requests that require authorization
export const authAxios = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set up interceptor for authenticated requests to automatically add token
authAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Tells account-scoped caches (Apollo, see apollo-client.ts) that the session changed.
// An event, not an import: this module must not pull in the Apollo client.
export const ACCOUNT_CHANGED_EVENT = 'auth:account-changed';
function notifyAccountChanged() {
  window.dispatchEvent(new Event(ACCOUNT_CHANGED_EVENT));
}

// Set up response interceptor for authenticated requests to handle 401 errors
authAxios.interceptors.response.use(
  (response) => {
    notifyBackendAvailable();
    return response;
  },
  (error) => {
    if (shouldClearSessionOnError(error)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearProfilePicture(localStorage);
      notifyAccountChanged();
      window.dispatchEvent(new CustomEvent('auth:logout'));
    } else if (isBackendUnreachable(error)) {
      notifyBackendUnavailable();
    }
    return Promise.reject(error);
  }
);

// Helper function to set the auth token (called after successful login)
export const setAuthToken = (token: string) => {
  localStorage.setItem('token', token);
  notifyAccountChanged();
  // The authAxios interceptor will automatically use this token
};

// Helper function to clear the auth token (called on logout)
export const clearAuthToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  clearProfilePicture(localStorage);
  notifyAccountChanged();
  // The authAxios interceptor will automatically stop using the token
};
